import {
  ExpenseSortField,
  ExpensesQuery,
} from '../models/expense.model';

/** Columnas de la tabla, en el orden del diseño. */
export const EXPENSE_TABLE_COLUMNS = [
  'date',
  'orderHash',
  'client',
  'driver',
  'licensePlate',
  'category',
  'description',
  'amount',
  'status',
  'chargeback',
  'receipt',
  'actions',
] as const;

export type ExpenseTableColumn = (typeof EXPENSE_TABLE_COLUMNS)[number];

/**
 * Columna de tabla → campo de orden del contrato.
 * `client`, `description`, `chargeback`, `receipt` y `actions` no son ordenables.
 *
 * `client` y `chargeback` quedan fuera porque el contrato no admite ordenar por esos campos:
 * `SortBy` acepta `reportedAt`, `orderHash`, `driverName`, `licensePlate`, `category`, `amount`
 * y `status`.
 */
export const EXPENSE_SORTABLE_COLUMNS: Readonly<
  Partial<Record<ExpenseTableColumn, ExpenseSortField>>
> = {
  date: 'reportedAt',
  orderHash: 'orderHash',
  driver: 'driverName',
  licensePlate: 'licensePlate',
  category: 'category',
  amount: 'amount',
  status: 'status',
};

/**
 * Diez por página: con 25 la tabla obligaba a hacer scroll para llegar al pie y
 * al paginador. La API acepta hasta 200 y usa 25 por defecto.
 */
export const EXPENSES_PAGE_SIZE = 10;

/**
 * Tope de páginas al recorrer el listado completo para exportar. La API no tiene
 * endpoint de export, así que se pagina; el tope evita colgar el navegador y el
 * contenedor avisa cuando se alcanza en lugar de truncar en silencio.
 */
export const EXPENSES_EXPORT_MAX_PAGES = 25;

/** Debounce del buscador, alineado con el resto de tablas de la app. */
export const EXPENSES_SEARCH_DEBOUNCE_MS = 400;

export const EXPENSES_DEFAULT_QUERY: ExpensesQuery = {
  searchTerm: '',
  fromDate: null,
  toDate: null,
  categories: [],
  statuses: [],
  zoneIds: [],
  clientIds: [],
  page: 1,
  pageSize: EXPENSES_PAGE_SIZE,
  sort: { field: 'reportedAt', direction: 'desc' },
};

/** Divisa por defecto al crear un gasto desde la plataforma. */
export const EXPENSES_DEFAULT_CURRENCY = 'EUR';

export const RECEIPT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * El backend sólo acepta png, jpg y jpeg para el comprobante: cualquier otro
 * formato se rechazaría en servidor, así que se filtra antes de subirlo.
 */
export const RECEIPT_ACCEPTED_MIME_TYPES: readonly string[] = [
  'image/jpeg',
  'image/png',
] as const;

export const RECEIPT_ACCEPT_ATTRIBUTE = '.jpg,.jpeg,.png';
